import * as React from 'react';
import { cn } from '@noor/lib/cn';

interface PageHeaderProps {
  /** Page title — rendered as the H1. */
  title: string;
  /** Optional supporting line beneath the title. */
  description?: string;
  /**
   * Optional leading slot rendered before the title — typically a back
   * `IconButton` on detail pages. Kept as a slot (not a boolean) so the app
   * owns routing/navigation; `@noor/ui` stays domain-agnostic.
   */
  back?: React.ReactNode;
  /** Optional action slot (buttons, etc.) aligned to the trailing edge. */
  actions?: React.ReactNode;
  className?: string;
}

/**
 * The standard page heading block — one H1 + a muted description, with an
 * optional leading back slot and a trailing action slot. Centralizes the title
 * pattern that every page repeats, so the type scale and spacing stay
 * consistent everywhere. Presentational only (CLAUDE.md: shared/ui knows
 * nothing about domains).
 */
export function PageHeader({
  title,
  description,
  back,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <div
      data-slot="page-header"
      className={cn(
        'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        {back && <div className="shrink-0">{back}</div>}
        <div className="min-w-0 space-y-1">
          <h1 className="text-xl leading-[26px] font-semibold tracking-[-0.4px] text-foreground">
            {title}
          </h1>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
